import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Info,
  Save,
  ToggleLeft,
  ToggleRight,
  Layout,
  Code,
  CheckCircle,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Github as Pinterest,
  Wand2,
  RotateCcw,
  Eye,
  Edit3,
} from "lucide-react";
import { toast } from "react-toastify";

interface Props {
  template: any;
  onSave: (data: any) => void;
}

const mockData: any = {
  name: "Achyut Thapa",
  email: "achyut@zedfo.com",
  store_name: "Zedfo Supermarket",
  amount: "Rs 150.00",
  withdrawal_amount: "Rs 500.00",
  reason: "Account verification successfully completed.",
  reset_url: "https://zedfo.com/reset-password?token=mock_token",
  login_url: "https://zedfo.com/login",
  phone: "+9800000000",
  otp: "4829",
  order_id: "#ORD-97721",
  total_amount: "Rs 2,450.50",
  status: "Approved",
};

const autoTemplates: any = {
  admin: {
    forgot_password: {
      subject: "Admin Password Reset - Zedfo",
      body: '<h1>Password Reset Request</h1><p>Hello {{name}},</p><p>You requested a password reset for your admin account. Please click the link below to set a new password:</p><p><a href="{{reset_url}}" target="_blank">Reset Password Now</a></p><p>If you did not request this, please ignore this email.</p>',
    },
    new_store_registration: {
      subject: "Action Required: New Store Registered",
      body: '<h1>New Store Alert</h1><p>A new store <strong>{{store_name}}</strong> has just completed registration. All required documents have been uploaded and are ready for your review.</p><p><a href="{{login_url}}" target="_blank">Go to Admin Panel</a></p>',
    },
    new_deliveryman_registration: {
      subject: "Action Required: New Delivery Man Registered",
      body: '<h1>New Delivery Man Alert</h1><p>A new delivery man <strong>{{name}}</strong> ({{phone}}) has just registered. Please verify their identity and vehicle documents in the admin portal.</p><p><a href="{{login_url}}" target="_blank">Verify Now</a></p>',
    },
    withdraw_request: {
      subject: "New Store Withdraw Request - {{amount}}",
      body: '<h1>Withdrawal Request</h1><p>A withdrawal request has been initiated by <strong>{{store_name}}</strong> for the amount of <strong>{{amount}}</strong>. Please review and process the payout.</p><p><a href="{{login_url}}" target="_blank">View Request</a></p>',
    },
    deliveryman_withdraw_request: {
      subject: "New Delivery Man Withdraw Request - {{amount}}",
      body: '<h1>Withdrawal Request</h1><p>Delivery personnel <strong>{{name}}</strong> has requested a withdrawal of <strong>{{amount}}</strong>. Please check the transaction logs and authorize the payment.</p><p><a href="{{login_url}}" target="_blank">View Request</a></p>',
    },
  },
  store: {
    registration: {
      subject: "Welcome to Zedfo - Registration Received!",
      body: "<h1>Registration Successful</h1><p>Hello {{name}},</p><p>Thank you for choosing Zedfo. We have received your registration for <strong>{{store_name}}</strong>. Our team is currently performing a standard verification of your details.</p><p>You will receive an updated status email within 24-48 hours. Stay tuned!</p>",
    },
    approval: {
      subject: "Your Store Account is Approved!",
      body: '<h1>Congratulations!</h1><p>Hi {{name}},</p><p>Great news! Your store <strong>{{store_name}}</strong> has been approved. You can now access your dashboard to set up your profile and start adding products.</p><p><a href="{{login_url}}" target="_blank">Access Your Dashboard</a></p>',
    },
    rejection: {
      subject: "Store Registration Update",
      body: "<h1>Application Status</h1><p>Dear {{name}},</p><p>We regret to inform you that your application for <strong>{{store_name}}</strong> has been declined at this time.</p><p><strong>Reason:</strong> {{reason}}</p><p>You may resubmit your application after addressing the issues mentioned above.</p>",
    },
    suspension: {
      subject: "Important: Account Suspension Notice",
      body: "<h1>Account Terminated/Suspended</h1><p>Hello {{name}},</p><p>We have noticed some irregularities with your store <strong>{{store_name}}</strong>. As a result, your account has been suspended until further notice.</p><p><strong>Reason:</strong> {{reason}}</p><p>Please contact our support team if you believe this is a mistake.</p>",
    },
    withdrawal_approved: {
      subject: "Withdrawal Request Approved - {{amount}}",
      body: "<h1>Payment Processed</h1><p>Hi {{name}},</p><p>Your withdrawal request for <strong>{{amount}}</strong> has been approved! The funds have been transferred to your registered bank account.</p><p>Please allow 1-3 business days for the amount to reflect.</p>",
    },
    withdrawal_rejected: {
      subject: "Withdrawal Request Declined",
      body: "<h1>Withdrawal Update</h1><p>Hello {{name}},</p><p>We were unable to process your withdrawal request for <strong>{{amount}}</strong>.</p><p><strong>Reason:</strong> {{reason}}</p><p>The amount has been credited back to your wallet balance. Please correct the details and try again.</p>",
    },
    forgot_password: {
      subject: "Reset Your Zedfo Store Password",
      body: '<h1>Password Recovery</h1><p>Hi {{name}},</p><p>Click the link below to securely reset your password for the store <strong>{{store_name}}</strong>.</p><p><a href="{{reset_url}}" target="_blank">Reset Password</a></p>',
    },
  },
  deliveryman: {
    registration: {
      subject: "Zedfo Partner Registration - Successful",
      body: "<h1>Application Received</h1><p>Hi {{name}},</p><p>Thank you for your interest in joining Zedfo as a Delivery Partner. We've received your documents and are currently reviewing them.</p><p>We'll reach out to you once the verification steps are complete.</p>",
    },
    approval: {
      subject: "Welcome to the Fleet - Account Approved!",
      body: "<h1>You're All Set!</h1><p>Hello {{name}},</p><p>Your partner account has been approved. You can now log into the delivery app and start accepting orders.</p><p>Happy earning!</p>",
    },
    rejection: {
      subject: "Delivery Partner Application Update",
      body: "<h1>Application Status</h1><p>Hi {{name}},</p><p>Thank you for applying. Currently, we are unable to move forward with your application.</p><p><strong>Reason:</strong> {{reason}}</p><p>Best of luck with your future endeavors.</p>",
    },
    suspension: {
      subject: "Security Alert: Delivery Account Suspended",
      body: "<h1>Partner Account Update</h1><p>Hello {{name}},</p><p>Your delivery partner account has been suspended due to recent activities.</p><p><strong>Reason:</strong> {{reason}}</p><p>Please visit the main office for more information.</p>",
    },
    withdrawal_approved: {
      subject: "Earnings Paid Out - {{amount}}",
      body: "<h1>Payment Sent</h1><p>Hi {{name}},</p><p>Your earnings totaling <strong>{{amount}}</strong> have been approved for payout. Check your bank account for the deposit.</p>",
    },
    withdrawal_rejected: {
      subject: "Payment Request Update",
      body: "<h1>Withdrawal Issue</h1><p>Hello {{name}},</p><p>We encountered an issue while processing your payout of <strong>{{amount}}</strong>.</p><p><strong>Reason:</strong> {{reason}}</p>",
    },
    forgot_password: {
      subject: "Reset Your Partner Password",
      body: '<h1>Security Check</h1><p>Hello {{name}},</p><p>Please use the button below to reset your partner account password.</p><p><a href="{{reset_url}}" target="_blank">Reset Password</a></p>',
    },
  },
  customer: {
    registration: {
      subject: "Welcome to Zedfo Shopping!",
      body: '<h1>Welcome Home</h1><p>Hi {{name}},</p><p>Thank you for choosing Zedfo. Discover millions of products at the best prices. Your account is now active and ready for your first order!</p><p><a href="{{login_url}}" target="_blank">Shop Now</a></p>',
    },
    otp_verification: {
      subject: "Zedfo Verification Code: {{otp}}",
      body: '<h1>Verification Code</h1><p>Hi {{name}},</p><p>Your security code for verification is: <strong style="font-size: 24px; color: #3182ce;">{{otp}}</strong></p><p>This code is valid for only 10 minutes. Do not share this code with anyone.</p>',
    },
    forgot_password: {
      subject: "Reset Your Zedfo Password",
      body: '<h1>Account Recovery</h1><p>Hello {{name}},</p><p>Need a new password? No problem. Use the link below to get back into your account.</p><p><a href="{{reset_url}}" target="_blank">Reset Password</a></p>',
    },
    order_confirmation: {
      subject: "Confirmation: Order {{order_id}} has been placed!",
      body: "<h1>Order Confirmed</h1><p>Hi {{name}},</p><p>We've successfully received your order <strong>{{order_id}}</strong>. Our fulfillment team is now preparing your items for delivery.</p><p><strong>Total Amount:</strong> {{total_amount}}</p><p>We will notify you once your order is shipped!</p>",
    },
    suspension: {
      subject: "Zedfo Account Status Update",
      body: "<h1>Member Account Update</h1><p>Hello {{name}},</p><p>Notice: Your customer account access has been restricted.</p><p><strong>Reason:</strong> {{reason}}</p>",
    },
    cash_collection: {
      subject: "Payment Received Notification",
      body: "<h1>Payment Success</h1><p>Hi {{name}},</p><p>We confirm that cash collection of <strong>{{amount}}</strong> for order <strong>{{order_id}}</strong> has been successfully completed and recorded.</p><p>Thank you for shopping with us!</p>",
    },
  },
};

const variableGuide: any = {
  admin: ["name", "email", "store_name", "amount", "reason", "reset_url"],
  store: [
    "name",
    "store_name",
    "status",
    "reason",
    "withdrawal_amount",
    "reset_url",
    "login_url",
  ],
  deliveryman: [
    "name",
    "phone",
    "status",
    "reason",
    "withdrawal_amount",
    "reset_url",
  ],
  customer: [
    "name",
    "email",
    "phone",
    "otp",
    "order_id",
    "total_amount",
    "reset_url",
  ],
};

const TemplateEditor: React.FC<Props> = ({ template, onSave }) => {
  const [formData, setFormData] = useState<any>({
    subject: "",
    body: "",
    isActive: true,
    footer: {
      sectionText: "",
      pages: { privacy: true, refund: true, cancelation: true, contact: true },
      social: {
        facebook: true,
        instagram: true,
        twitter: true,
        linkedin: true,
        pinterest: true,
      },
      copyright: "",
    },
  });

  useEffect(() => {
    if (template) {
      setFormData({
        subject: template.subject || "",
        body: template.body || "",
        isActive: template.isActive !== undefined ? template.isActive : true,
        footer: template.footer || {
          sectionText:
            "Please contact us for any queries; we're always happy to help.",
          pages: {
            privacy: true,
            refund: true,
            cancelation: true,
            contact: true,
          },
          social: {
            facebook: true,
            instagram: true,
            twitter: true,
            linkedin: true,
            pinterest: true,
          },
          copyright: `© ${new Date().getFullYear()} Zedfo. All rights reserved.`,
        },
      });
    }
  }, [template]);

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleFooterChange = (key: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      footer: { ...prev.footer, [key]: value },
    }));
  };

  const handleFooterToggle = (category: "pages" | "social", key: string) => {
    setFormData((prev: any) => ({
      ...prev,
      footer: {
        ...prev.footer,
        [category]: {
          ...prev.footer[category],
          [key]: !prev.footer[category][key],
        },
      },
    }));
  };

  const handleAutoGenerate = () => {
    const preset = autoTemplates[template.type]?.[template.event];
    if (preset) {
      setFormData((prev: any) => ({
        ...prev,
        subject: preset.subject,
        body: preset.body,
      }));
      toast.info("Generated professional template correctly!");
    } else {
      toast.warning("No preset found for this event yet.");
    }
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all changes?")) {
      setFormData({
        subject: template.subject || "",
        body: template.body || "",
        isActive: template.isActive !== undefined ? template.isActive : true,
        footer: template.footer || {
          sectionText:
            "Please contact us for any queries; we're always happy to help.",
          pages: {
            privacy: true,
            refund: true,
            cancelation: true,
            contact: true,
          },
          social: {
            facebook: true,
            instagram: true,
            twitter: true,
            linkedin: true,
            pinterest: true,
          },
          copyright: `© ${new Date().getFullYear()} Zedfo. All rights reserved.`,
        },
      });
    }
  };

  const handleSave = () => {
    // Only send necessary fields to avoid conflicts with server-managed fields (id, dates)
    const { id, createdAt, updatedAt, ...cleanData } = formData;
    onSave({
      ...template,
      ...cleanData,
    });
  };

  const getPreviewHtml = () => {
    if (!template) return formData.body;
    let html = formData.body;
    const variables = variableGuide[template.type] || [];
    variables.forEach((v: string) => {
      const regex = new RegExp(`{{${v}}}`, "g");
      const mockVal = mockData[v] || `[${v.toUpperCase()}]`;
      html = html.replace(
        regex,
        `<span style="color: #3182ce; font-weight: bold;">${mockVal}</span>`,
      );
    });
    return html;
  };

  const getPreviewSubject = () => {
    if (!template) return formData.subject;
    let sub = formData.subject;
    const variables = variableGuide[template.type] || [];
    variables.forEach((v: string) => {
      const regex = new RegExp(`{{${v}}}`, "g");
      const mockVal = mockData[v] || `[${v.toUpperCase()}]`;
      sub = sub.replace(regex, mockVal);
    });
    return sub;
  };

  return (
    <EditorContainer>
      <EditorHeader>
        <div className="right">
          <StatusToggle
            isActive={formData.isActive}
            onClick={() => handleChange("isActive", !formData.isActive)}
          >
            {formData.isActive ? (
              <ToggleRight size={24} />
            ) : (
              <ToggleLeft size={24} />
            )}
            <span>{formData.isActive ? "Active" : "Inactive"}</span>
          </StatusToggle>
          <ActionButton onClick={handleAutoGenerate} variant="secondary">
            <Wand2 size={16} /> Smart Auto-Generate
          </ActionButton>
          <ActionButton onClick={handleReset} variant="outline">
            <RotateCcw size={16} /> Reset
          </ActionButton>
          <SaveButton onClick={handleSave}>
            <Save size={16} /> Save Changes
          </SaveButton>
        </div>
      </EditorHeader>

      <MainLayout>
        <ContentArea>
          <PaneHeader>
            <Edit3 size={16} /> Editor
          </PaneHeader>
          <EditForm>
            <FormGroup>
              <Label>Email Subject</Label>
              <Input
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                placeholder="Enter email subject line..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Email Body (HTML)</Label>
              <QuillWrapper>
                <ReactQuill
                  theme="snow"
                  value={formData.body}
                  onChange={(content) => handleChange("body", content)}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "image"],
                      ["clean"],
                    ],
                  }}
                />
              </QuillWrapper>
            </FormGroup>

            <FooterContentSection>
              <SectionHeader>
                <Layout size={18} />
                <span>Footer Content</span>
              </SectionHeader>

              <FormGroup>
                <Label>Section Text(Default)</Label>
                <Input
                  value={formData.footer.sectionText}
                  onChange={(e) =>
                    handleFooterChange("sectionText", e.target.value)
                  }
                  placeholder="Please contact us for any queries..."
                />
              </FormGroup>

              <FormGroup>
                <Label>Page Links</Label>
                <CheckboxGroup>
                  <CheckboxItem
                    onClick={() => handleFooterToggle("pages", "privacy")}
                  >
                    <Checkbox active={formData.footer.pages.privacy} />
                    <span>Privacy Policy</span>
                  </CheckboxItem>
                  <CheckboxItem
                    onClick={() => handleFooterToggle("pages", "refund")}
                  >
                    <Checkbox active={formData.footer.pages.refund} />
                    <span>Refund Policy</span>
                  </CheckboxItem>
                  <CheckboxItem
                    onClick={() => handleFooterToggle("pages", "cancelation")}
                  >
                    <Checkbox active={formData.footer.pages.cancelation} />
                    <span>Cancelation Policy</span>
                  </CheckboxItem>
                  <CheckboxItem
                    onClick={() => handleFooterToggle("pages", "contact")}
                  >
                    <Checkbox active={formData.footer.pages.contact} />
                    <span>Contact Us</span>
                  </CheckboxItem>
                </CheckboxGroup>
              </FormGroup>

              <FormGroup>
                <Label>Social Media Links</Label>
                <CheckboxGroup>
                  {[
                    "facebook",
                    "instagram",
                    "twitter",
                    "linkedin",
                    "pinterest",
                  ].map((social) => (
                    <CheckboxItem
                      key={social}
                      onClick={() => handleFooterToggle("social", social)}
                    >
                      <Checkbox active={formData.footer.social[social]} />
                      <span style={{ textTransform: "capitalize" }}>
                        {social}
                      </span>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </FormGroup>

              <FormGroup>
                <Label>Copyright Content(Default)</Label>
                <Input
                  value={formData.footer.copyright}
                  onChange={(e) =>
                    handleFooterChange("copyright", e.target.value)
                  }
                  placeholder="© 2026 Zedfo. All rights reserved."
                />
              </FormGroup>
            </FooterContentSection>
          </EditForm>
        </ContentArea>

        {/* MIDDLE: LIVE PREVIEW */}
        <PreviewArea>
          <PaneHeader>
            <Eye size={16} /> Live Preview (Realistic Data)
          </PaneHeader>
          <PreviewContainer>
            <EmailWrapper>
              <EmailHeader>
                <LogoCircle>
                  <CheckCircle size={32} color="#38a169" />
                </LogoCircle>
                <EmailSubjectHeader>
                  {getPreviewSubject() || "No Subject Defined"}
                </EmailSubjectHeader>
              </EmailHeader>

              <EmailCard>
                <div
                  className="preview-body"
                  dangerouslySetInnerHTML={{
                    __html:
                      getPreviewHtml() ||
                      "<p>Start typing to see preview...</p>",
                  }}
                />
                <GeneratedLinkWrapper>
                  <a href="#">Generated link</a>
                </GeneratedLinkWrapper>

                <Divider />

                <SupportSection>
                  {formData.footer.sectionText && (
                    <p>{formData.footer.sectionText}</p>
                  )}
                  <p>Thanks & Regards,</p>
                  <p>
                    <strong>Zedfo Team</strong>
                  </p>
                </SupportSection>

                <FooterBranding>
                  <BrandingLogo>
                    <div className="logo-box">Z</div>
                    <span>Zedfo</span>
                  </BrandingLogo>
                  <FooterLinks>
                    {formData.footer.pages.privacy && (
                      <>
                        <a href="#">Privacy Policy</a>
                        <span>•</span>
                      </>
                    )}
                    {formData.footer.pages.refund && (
                      <>
                        <a href="#">Refund Policy</a>
                        <span>•</span>
                      </>
                    )}
                    {formData.footer.pages.cancelation && (
                      <>
                        <a href="#">Cancelation Policy</a>
                        <span>•</span>
                      </>
                    )}
                    {formData.footer.pages.contact && (
                      <a href="#">Contact us</a>
                    )}
                  </FooterLinks>
                  <SocialIcons>
                    {formData.footer.social.facebook && <Facebook size={18} />}
                    {formData.footer.social.instagram && (
                      <Instagram size={18} />
                    )}
                    {formData.footer.social.twitter && <Twitter size={18} />}
                    {formData.footer.social.linkedin && <Linkedin size={18} />}
                    {formData.footer.social.pinterest && (
                      <Pinterest size={18} />
                    )}
                  </SocialIcons>
                  <Copyright>{formData.footer.copyright}</Copyright>
                </FooterBranding>
              </EmailCard>
            </EmailWrapper>
          </PreviewContainer>
        </PreviewArea>

        {/* RIGHT: VARIABLES */}
        <SidePanel>
          <PanelTitle>
            <Info size={16} /> Available Variables
          </PanelTitle>
          <VariableList>
            {template &&
              variableGuide[template.type]?.map((v: string) => (
                <VariableItem
                  key={v}
                  onClick={() => {
                    handleChange("body", formData.body + ` {{${v}}} `);
                  }}
                >
                  <code>{`{{${v}}}`}</code>
                  <span>Click to add</span>
                </VariableItem>
              ))}
          </VariableList>
          <PanelNote>
            Use these placeholders to dynamically insert real user data into the
            email content.
          </PanelNote>
        </SidePanel>
      </MainLayout>
    </EditorContainer>
  );
};

const EditorContainer = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EditorHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: end;
  align-items: center;
  background: #f8fafc;
  .left,
  .right {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1a202c;
  font-weight: 700;
  font-size: 16px;
`;

const ActionButton = styled.button<{ variant?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  ${(props) =>
    props.variant === "secondary" &&
    `
    background: #ebf8ff;
    color: #3182ce;
    border: 1px solid #3182ce;
    &:hover { background: #bee3f8; }
  `}
  ${(props) =>
    props.variant === "outline" &&
    `
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    &:hover { background: #f7fafc; }
  `}
`;

const StatusToggle = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: ${(props) => (props.isActive ? "#38a169" : "#e53e3e")};
  font-weight: 600;
  font-size: 14px;
  padding: 0 12px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3182ce;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #2b6cb0;
  }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 280px;
  min-height: 700px;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr 280px;
  }
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  color: #4a5568;
`;

const ContentArea = styled.div`
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PreviewArea = styled.div`
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  background: #f1f5f9;
  min-width: 0;
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 16px;
  outline: none;
  &:focus {
    border-color: #3182ce;
  }
`;

const QuillWrapper = styled.div`
  .ql-container {
    height: 300px;
    font-size: 16px;
  }
  .ql-editor {
    min-height: 300px;
  }
`;

const FooterContentSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2d3748;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  span {
    font-size: 13px;
    color: #4a5568;
    user-select: none;
  }
`;

const Checkbox = styled.div<{ active: boolean }>`
  width: 16px;
  height: 16px;
  border: 2px solid ${(props) => (props.active ? "#3182ce" : "#cbd5e0")};
  background: ${(props) => (props.active ? "#3182ce" : "white")};
  border-radius: 4px;
  position: relative;
  transition: all 0.2s;
  &::after {
    content: "✓";
    position: absolute;
    color: white;
    font-size: 10px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: ${(props) => (props.active ? 1 : 0)};
  }
`;

const SidePanel = styled.div`
  padding: 24px;
  background: #f7fafc;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const VariableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VariableItem = styled.div`
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
  &:hover {
    border-color: #3182ce;
    background: #ebf8ff;
  }
  code {
    font-size: 13px;
    color: #3182ce;
    font-weight: 600;
  }
  span {
    font-size: 11px;
    color: #718096;
  }
`;

const PanelNote = styled.p`
  margin-top: 20px;
  font-size: 12px;
  color: #718096;
  line-height: 1.5;
`;

const PreviewContainer = styled.div`
  padding: 30px;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  max-height: calc(100vh - 180px);
`;

const EmailWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EmailHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const LogoCircle = styled.div`
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EmailSubjectHeader = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const EmailCard = styled.div`
  background: white;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  .preview-body {
    font-size: 14px;
    color: #4a5568;
    line-height: 1.6;
    margin-bottom: 20px;
    word-break: break-word;
    h1,
    h2,
    h3 {
      color: #1a202c;
      margin-bottom: 12px;
    }
    p {
      margin-bottom: 10px;
    }
    a {
      word-break: break-all;
    }
  }
`;

const GeneratedLinkWrapper = styled.div`
  margin-bottom: 20px;
  a {
    color: #3182ce;
    font-weight: 600;
    text-decoration: none;
    font-size: 14px;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #edf2f7;
  width: 100%;
  margin-bottom: 20px;
`;

const SupportSection = styled.div`
  font-size: 13px;
  color: #718096;
  margin-bottom: 30px;
  p {
    margin-bottom: 6px;
  }
`;

const FooterBranding = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const BrandingLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  .logo-box {
    background: #38a169;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    font-weight: 900;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  span {
    font-size: 16px;
    font-weight: 800;
    color: #38a169;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #4a5568;
  margin-bottom: 16px;
  flex-wrap: wrap;
  justify-content: center;
  a {
    color: #4a5568;
    text-decoration: none;
    font-weight: 600;
    &:hover {
      color: #3182ce;
    }
  }
  span {
    color: #e2e8f0;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 12px;
  color: #718096;
  margin-bottom: 16px;
  svg {
    cursor: pointer;
    &:hover {
      color: #3182ce;
    }
  }
`;

const Copyright = styled.div`
  font-size: 11px;
  color: #a0aec0;
`;

export default TemplateEditor;
